import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../store/store';
import {
  extractSpotifyArtistIdFromComposer,
  getKnownComposerIds,
  loadCatalogComposerIds,
  registerComposerMapping,
} from './composerRegistry';

export const useComposerIdSet = (): ReadonlySet<string> => {
  const composers = useAppSelector((state) => state.yourLibrary.myComposers);
  const [catalogIds, setCatalogIds] = useState<ReadonlySet<string>>(() => new Set(getKnownComposerIds()));

  useEffect(() => {
    let cancelled = false;
    loadCatalogComposerIds()
      .then((ids) => {
        if (cancelled) return;
        if (!ids) return;
        if (ids.size === 0) return;
        setCatalogIds((prev) => {
          const merged = new Set(prev);
          ids.forEach((id) => merged.add(id));
          return merged;
        });
      })
      .catch(() => {
        // Swallow errors; registry already logged in development.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const ids = new Set<string>(catalogIds);

    // Include any globally known composers that may have been added elsewhere (e.g. dynamic lookups).
    getKnownComposerIds().forEach((id) => ids.add(id));

    composers.forEach((composer) => {
      const spotifyId = extractSpotifyArtistIdFromComposer(composer as Record<string, any>);
      if (!spotifyId) return;
      ids.add(spotifyId);
      const catalogId =
        (composer as Record<string, any>)?.id ??
        (composer as Record<string, any>)?.composerId ??
        (composer as Record<string, any>)?.composer_id;
      registerComposerMapping(spotifyId, catalogId);
    });

    return ids;
  }, [catalogIds, composers]);
};
